import { useState } from "react";
import type { NodeDTO, NodeServiceDTO } from "@roomate/core";
import { useMe, useNodes, useServiceCheck } from "../../api.ts";
import { Block, Card, Pill } from "../../calm.tsx";
import { Ago, Button } from "../../ui.tsx";
import { serviceChip, serviceFix, serviceTitle } from "./service-state.ts";

/** Shown under every list (machine-connected-services §8). */
export const SERVICES_NOTE = "Roomate uses the logins already on this machine. It never logs in for you and never stores these credentials.";

function CopyCommand({ text, command, machine, testId }: { text: string; command: string | null; machine: string; testId: string }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    if (!command) return;
    navigator.clipboard?.writeText(command).then(
      () => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      },
      () => {},
    );
  };
  // "Could not check" has no command: show the catalog's advice as plain text, with nothing to copy.
  if (!command) {
    return <p className="mt-2 text-[12px] text-ink-3" data-testid={testId}>{text}</p>;
  }
  return (
    <div className="mt-2" data-testid={testId}>
      <p className="text-[12px] text-ink-3">Run this in a terminal on {machine}</p>
      <div className="mt-1 flex items-start gap-2">
        <pre className="min-w-0 flex-1 overflow-x-auto rounded-md border border-rule bg-paper p-2 font-mono text-[12px]">{command}</pre>
        <Button type="button" onClick={copy} data-testid={`${testId}-copy`}>{copied ? "Copied" : "Copy"}</Button>
      </div>
    </div>
  );
}

/** One service row: chip, account (admins only), last checked, and the catalog's fix command when it is not ready. */
export function ServiceRow({ service, machine, admin, nodeId }: { service: NodeServiceDTO; machine: string; admin: boolean; nodeId: string }) {
  const chip = serviceChip(service.state);
  const fix = serviceFix(service);
  const id = `service-${nodeId}-${service.name}-${service.scope}`;
  return (
    <li className="px-5 py-3" data-testid={id}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]">
        <span className="font-medium text-ink">{serviceTitle(service.name)}</span>
        <Pill tone={chip.tone} testId={`${id}-state`}>{chip.label}</Pill>
        {admin && service.label && <span className="text-ink-2" data-testid={`${id}-account`}>{service.label}</span>}
        <span className="ml-auto text-[12px] text-ink-3">checked <Ago ts={service.checkedAt} /></span>
      </div>
      {fix && <CopyCommand text={fix.text} command={fix.command} machine={machine} testId={`${id}-fix`} />}
    </li>
  );
}

/** A machine's "Connected services" list. Presentational: no data hooks, so it renders without a query client. */
export function NodeServicesCard({ node, admin, canCheck = true, onCheck, checking = false, error = null }: { node: NodeDTO; admin: boolean; canCheck?: boolean; onCheck: () => void; checking?: boolean; error?: string | null }) {
  const services = node.services ?? [];
  return (
    <Card pad={false}>
      <div className="flex items-center justify-between gap-3 border-b border-rule px-5 py-3 text-[13px]">
        <span className="font-medium text-ink">{node.name}</span>
        <div className="flex items-center gap-2">
          <Pill tone={node.online ? "live" : "neutral"}>{node.online ? "online" : "offline"}</Pill>
          {canCheck && (
            <Button type="button" onClick={onCheck} disabled={checking} data-testid={`service-check-${node.id}`}>
              {checking ? "Checking…" : "Check again"}
            </Button>
          )}
        </div>
      </div>
      {error && <p className="border-b border-rule px-5 py-2 text-[12px] text-high" role="alert" data-testid={`service-check-error-${node.id}`}>{error}</p>}
      {services.length === 0 ? (
        <p className="px-5 py-4 text-[13px] text-ink-3" data-testid={`services-empty-${node.id}`}>
          This machine has not reported any connected services yet.
        </p>
      ) : (
        <ul className="divide-y divide-rule/60" data-testid={`services-${node.id}`}>
          {services.map((s) => (
            <ServiceRow key={`${s.name}:${s.scope}`} service={s} machine={node.name} admin={admin} nodeId={node.id} />
          ))}
        </ul>
      )}
    </Card>
  );
}

/** Team page block: every machine in the office with its Connected services. */
export function NodeServices() {
  const me = useMe().data;
  const nodes = useNodes().data ?? [];
  const check = useServiceCheck();
  const admin = me?.access === "admin";
  if (nodes.length === 0) return null;
  return (
    <Block title="Connected services" aside="What each machine is logged in to">
      <div className="space-y-4" data-testid="node-services">
        {nodes.map((node) => (
          <NodeServicesCard
            key={node.id}
            node={node}
            admin={admin}
            canCheck={admin || node.memberId === me?.id}
            onCheck={() => check.mutate(node.id)}
            checking={check.isPending && check.variables === node.id}
            error={check.isError && check.variables === node.id ? check.error.message : null}
          />
        ))}
        <p className="text-[12px] text-ink-3" data-testid="services-note">{SERVICES_NOTE}</p>
      </div>
    </Block>
  );
}
